import {computed, Ref} from "vue";


const adjustBrightness = (hex: string, amount: number) => {
  let usePound = false;
  if (hex[0] === "#") {
    hex = hex.slice(1);
    usePound = true;
  }

  // Support short hex #FFF
  if (hex.length === 3) {
    hex = hex.split('').map(c => c + c).join('');
  }

  // Basic hex validation
  if (!/^[0-9A-Fa-f]{6}$/.test(hex)) return `#${hex}`;

  const num = parseInt(hex, 16);
  let r = (num >> 16) + amount;
  let g = ((num >> 8) & 0x00FF) + amount;
  let b = (num & 0x0000FF) + amount;

  r = Math.max(Math.min(255, r), 0);
  g = Math.max(Math.min(255, g), 0);
  b = Math.max(Math.min(255, b), 0);

  const newHex = [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('');

  return (usePound ? "#" : "") + newHex;
};


export function applyDynamicSidebarColor(hexColor: string) {
  if (!hexColor) return;

  const styleId = 'dynamic-sidebar-theme';
  let styleEl = document.getElementById(styleId);

  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = styleId;
    document.head.appendChild(styleEl);
  }

  const sidebarHover = adjustBrightness(hexColor, -30);

  styleEl.innerHTML = `
    :root {
      --app-dynamic-sidebar: ${hexColor};
      --app-dynamic-sidebar-icon: ${hexColor};
      --app-dynamic-sidebar-hover: ${sidebarHover};
    }
  `;
}

export function applyDynamicSidebarIconColor(hexColor: string) {
  if (!hexColor) return;

  const styleId = 'dynamic-sidebar-icon-theme';
  let styleEl = document.getElementById(styleId);

  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = styleId;
    document.head.appendChild(styleEl);
  }

  const sidebarHover = adjustBrightness(hexColor, -30);

  styleEl.innerHTML = `
    :root {
      --app-dynamic-sidebar-icon: ${hexColor};
      --app-dynamic-sidebar-icon-hover: ${sidebarHover};
    }
  `;
}

export function applyDynamicSidebarLinkColor(hexColor: string) {
  if (!hexColor) return;

  const styleId = 'dynamic-sidebar-link-theme';
  let styleEl = document.getElementById(styleId);

  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = styleId;
    document.head.appendChild(styleEl);
  }

  const sidebarHover = adjustBrightness(hexColor, -30);

  styleEl.innerHTML = `
    :root {
      --app-dynamic-sidebar-link: ${hexColor};
      --app-dynamic-sidebar-link-hover: ${sidebarHover};
    }
  `;
}

export function applyDynamicPrimaryColor(hexColor: string) {
  if (!hexColor) return;

  const styleId = 'dynamic-theme-overrides';
  let styleEl = document.getElementById(styleId);

  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = styleId;
    document.head.appendChild(styleEl);
  }

  // Generate slightly darkened variant for hovers
  const primaryHover = adjustBrightness(hexColor, -50);

  // Generate CSS rules to override default bootstrap and custom template settings
  styleEl.innerHTML = `
    :root {
      --primary: ${hexColor};
      --primary-hover: ${primaryHover};
    }

    .text-primary { color: var(--primary) !important; }
    .bg-primary { background-color: var(--primary) !important; }

    .btn-primary {
      background-color: var(--primary) !important;
      border-color: var(--primary) !important;
      color: #fff !important;
    }
    .btn-primary:hover,
    .btn-primary:focus,
    .btn-primary:active:focus,
    .btn-primary:active,
    .btn-primary.active,
    .show > .btn-primary.dropdown-toggle {
      background-color: var(--primary-hover) !important;
      border-color: var(--primary-hover) !important;
      color: #fff !important;
      box-shadow: none;
    }

    .btn-outline-primary {
      color: var(--primary) !important;
      border-color: var(--primary) !important;
    }
    .btn-outline-primary:hover,
    .btn-outline-primary:focus,
    .btn-outline-primary:active:focus,
    .btn-outline-primary:active,
    .btn-outline-primary.active,
    .show > .btn-outline-primary.dropdown-toggle {
      color: #fff !important;
      background-color: var(---primary) !important;
      border-color: var(--primary) !important;
    }
 
    .text-bg-primary, .bg-primary-light {
      background-color: var(--primary) !important;
    }

    .nav-pills .nav-link.active,
    .nav-pills .show > .nav-link {
      background-color: var(--primary) !important;
    }

    .page-item.active .page-link {
      background-color: var(--primary) !important;
      border-color: var(--primary) !important;
    }

    .form-check-input:checked {
      border-color: var(--primary) !important;
      background-color: var(--primary) !important;
    }

    .list-group-item.active {
      background-color: var(--primary) !important;
      border-color: var(--primary) !important;
    }

    /* Template specific classes */
    .menu-side-bright,
    .top-bar-bright,
    .user-popup-bg-bright {
      background-color: var(--primary) !important;
    }

    .menu-bright-icons {
      color: var(--primary) !important;
    }
  `;
}


export function useTenantColor(tenantKey: string) {
  const tenantColor = computed(() => {
    switch (tenantKey) {
      case 'bbq':
        return '#0064d6'

      case 'bsg':
        return '#2da555'

      default:
        return '#3498db'
    }
  })

  return {
    tenantColor
  }
}



